import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { StatusChip } from '../abf/components/StatusChip';
import { SmallButton } from '../abf/onboarding/review/contractReviewStyles';
import CrossFieldChecksPanel from './CrossFieldChecksPanel';
import type { BookingContract, TemplateEntry, TemplateJson, TemplateProblem } from './documentTemplatesApi';
import {
  CITATION_WORDS, FIELD_TYPES, type EntryRow, type EntrySection, entryKind, groupedEntries, pathFromName,
} from './templateModel';

const Group = styled.section`
  border: 1px solid #29374b; border-radius: 10px; background: #0c1524; margin-bottom: 16px; overflow: hidden;
  > h3 { padding: 12px 16px; background: #0f1b2b; border-bottom: 1px solid #29374b; font-size: 14px; }
  .entry { display: grid; grid-template-columns: minmax(180px, 1.1fr) minmax(130px, .7fr) minmax(160px, .9fr) minmax(0, 2fr) 84px;
    gap: 14px; align-items: start; padding: 12px 16px; }
  .entry + .entry, .editor { border-top: 1px solid #1f2b3a; }
  .entry.head { padding-top: 8px; padding-bottom: 8px; color: #91a5bd; font-size: 11px; font-weight: 650;
    letter-spacing: .05em; text-transform: uppercase; }
  @media (max-width: 1100px) { .entry.head { display: none; } }
  .entry[data-problem='true'] { background: #2a1717; }
  .name strong { display: block; font-size: 14px; }
  .name code, .key code { color: #91a5bd; font-size: 12px; }
  .tags { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 5px; }
  .kind, .key { color: #c5d4e6; font-size: 13px; }
  .key small, .kind small { display: block; color: #91a5bd; font-size: 12px; margin-top: 3px; }
  .instruction { color: #b6c7da; font-size: 13px; }
  .problem { color: #fca5a5; font-size: 12px; margin-top: 6px; }
  .tools { display: flex; gap: 6px; justify-content: flex-end; }
  .tools button { min-height: 32px; padding: 6px 8px; }
  @media (max-width: 1100px) { .entry { grid-template-columns: 1fr 1fr; } .tools { justify-content: flex-start; } }
`;

const Editor = styled.form`
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px 16px; padding: 16px; background: #101d2e;
  label { display: flex; flex-direction: column; gap: 5px; color: #b6c7da; font-size: 12px; }
  label.wide { grid-column: 1 / -1; }
  label.check { flex-direction: row; align-items: center; gap: 8px; }
  input, select, textarea { padding: 8px 10px; min-height: 38px; background: #142033; color: #e5edf7;
    border: 1px solid #33465e; border-radius: 6px; font-size: 13px; }
  textarea { min-height: 64px; resize: vertical; }
  .hint { color: #91a5bd; font-size: 12px; }
  .buttons { grid-column: 1 / -1; display: flex; gap: 10px; justify-content: flex-end; }
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;


const NUMERIC = ['money', 'percent', 'bps', 'number', 'integer'];

interface Draft {
  name: string;
  label: string;
  type: string;
  values: string;
  instruction: string;
  required: boolean;
  citation: 'REQUIRED' | 'OPTIONAL' | 'NONE';
  booking_key: string;
  review_group: string;
  min: string;
  max: string;
}

const toDraft = (entry: TemplateEntry | null, group: string): Draft => ({
  name: entry ? entry.path.replace(/^\//, '') : '',
  label: entry?.label || '',
  type: entry?.type || 'string',
  values: (entry?.values || []).join(', '),
  instruction: entry?.instruction || '',
  required: Boolean(entry?.required),
  citation: entry?.citation || 'OPTIONAL',
  booking_key: entry?.booking_key || '',
  review_group: entry?.review_group || group,
  min: entry?.validation?.min !== undefined ? String(entry.validation.min) : '',
  max: entry?.validation?.max !== undefined ? String(entry.validation.max) : '',
});

const applyDraft = (section: EntrySection, entry: TemplateEntry | null, draft: Draft): TemplateEntry => {
  const next: TemplateEntry = entry ? { ...entry } : { path: pathFromName(draft.name), label: draft.label.trim() };
  next.label = draft.label.trim();
  if (section === 'fields') {
    next.type = draft.type;
    if (draft.type === 'enum') next.values = draft.values.split(',').map(v => v.trim()).filter(Boolean);
    else delete next.values;
  }
  next.required = draft.required;
  next.citation = draft.citation;
  if (draft.instruction.trim()) next.instruction = draft.instruction.trim(); else delete next.instruction;
  if (draft.booking_key.trim()) next.booking_key = draft.booking_key.trim(); else delete next.booking_key;
  next.review_group = draft.review_group;
  if (section === 'fields') {
    const validation = { ...(next.validation || {}) };
    delete validation.min; delete validation.max;
    if (NUMERIC.includes(draft.type)) {
      if (draft.min.trim() !== '') validation.min = Number(draft.min);
      if (draft.max.trim() !== '') validation.max = Number(draft.max);
    }
    if (Object.keys(validation).length) next.validation = validation; else delete next.validation;
  }
  return next;
};

interface Props {
  template: TemplateJson;
  editable: boolean;
  contract?: BookingContract;
  problems: TemplateProblem[];
  onChange: (next: TemplateJson) => void;
}

/** The template as a reviewer reads it: one row per field, grouped as the review page groups them. */
const TemplateFieldsPanel: React.FC<Props> = ({ template, editable, contract, problems, onChange }) => {
  const [editing, setEditing] = useState<string | null>(null);   // `${section}:${index}` or 'new'
  const [draft, setDraft] = useState<Draft | null>(null);
  const groups = useMemo(() => groupedEntries(template), [template]);
  const groupNames = template.review_groups || [];

  // A problem's pointer names the entry by its position (/fields/3/booking_key).
  const problemsFor = (row: EntryRow) =>
    problems.filter(p => p.pointer === `/${row.section}/${row.index}` || p.pointer.startsWith(`/${row.section}/${row.index}/`));

  const open = (key: string, entry: TemplateEntry | null, group: string) => {
    setEditing(key);
    setDraft(toDraft(entry, group));
  };
  const close = () => { setEditing(null); setDraft(null); };

  const save = (section: EntrySection, index: number | null) => {
    if (!draft) return;
    const list = [...(template[section] || [])];
    if (index === null) list.push(applyDraft(section, null, draft));
    else list[index] = applyDraft(section, list[index], draft);
    onChange({ ...template, [section]: list });
    close();
  };

  const remove = (row: EntryRow) => {
    const list = [...(template[row.section] || [])];
    list.splice(row.index, 1);
    onChange({ ...template, [row.section]: list });
  };

  const set = (patch: Partial<Draft>) => setDraft(current => (current ? { ...current, ...patch } : current));

  const renderEditor = (section: EntrySection, index: number | null) => draft && (
    <Editor className="editor" onSubmit={event => { event.preventDefault(); save(section, index); }}>
      {index === null && (
        <label>
          Key the document is read into
          <input value={draft.name} onChange={e => set({ name: e.target.value })} placeholder="servicer_contact" required />
          <span className="hint">Saved as {pathFromName(draft.name || 'name')}</span>
        </label>
      )}
      <label>
        Label
        <input value={draft.label} onChange={e => set({ label: e.target.value })} required maxLength={80} />
      </label>
      {section === 'fields' && (
        <label>
          Type
          <select value={draft.type} onChange={e => set({ type: e.target.value })}>
            {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
      )}
      <label>
        Review group
        <select value={draft.review_group} onChange={e => set({ review_group: e.target.value })}>
          {groupNames.map(g => <option key={g} value={g}>{g}</option>)}
        </select>
      </label>
      {section === 'fields' && draft.type === 'enum' && (
        <label className="wide">
          Allowed values, separated by commas
          <input value={draft.values} onChange={e => set({ values: e.target.value })} required />
        </label>
      )}
      {section === 'fields' && NUMERIC.includes(draft.type) && (
        <>
          <label>Lowest allowed<input type="number" step="any" value={draft.min} onChange={e => set({ min: e.target.value })} /></label>
          <label>Highest allowed<input type="number" step="any" value={draft.max} onChange={e => set({ max: e.target.value })} /></label>
        </>
      )}
      <label>
        Citation
        <select value={draft.citation} onChange={e => set({ citation: e.target.value as Draft['citation'] })}>
          {Object.entries(CITATION_WORDS).map(([value, word]) => <option key={value} value={value}>{word}</option>)}
        </select>
      </label>
      <label>
        Books into
        <input list="booking-keys" value={draft.booking_key} onChange={e => set({ booking_key: e.target.value })}
          placeholder="Read only, not booked" />
        <span className="hint">Only keys the booking code reads are accepted.</span>
      </label>
      <label className="check">
        <input type="checkbox" checked={draft.required} onChange={e => set({ required: e.target.checked })} />
        The document must state it
      </label>
      <label className="wide">
        Instruction to the reader
        <textarea value={draft.instruction} onChange={e => set({ instruction: e.target.value })} maxLength={600}
          placeholder="What the value is, and where the document usually states it." />
      </label>
      <div className="buttons">
        <SmallButton type="button" onClick={close}><X size={14} /> Cancel</SmallButton>
        <SmallButton type="submit" style={{ borderColor: '#67c6f4' }}>{index === null ? 'Add field' : 'Keep change'}</SmallButton>
      </div>
    </Editor>
  );

  return (
    <div>
      <datalist id="booking-keys">
        {(contract?.allowedKeys || []).map(key => <option key={key} value={key} />)}
      </datalist>
      {groups.map(({ group, rows }) => (
        <Group key={group} aria-label={group}>
          <h3>{group}</h3>
          <div className="entry head" aria-hidden="true">
            <span>Field</span><span>Type and rules</span><span>Where it goes</span><span>Instruction to the reader</span><span />
          </div>
          {rows.map(row => {
            const key = `${row.section}:${row.index}`;
            const own = problemsFor(row);
            const { entry } = row;
            return (
              <React.Fragment key={key}>
                <div className="entry" data-problem={own.length > 0}>
                  <div className="name">
                    <strong>{entry.label}</strong>
                    <code>{entry.path}</code>
                    <div className="tags">
                      {entry.required && <StatusChip tone="warning" label="Must be stated" />}
                      {entry.topic && <StatusChip tone="neutral" label={`Long agreements: ${entry.topic}`} />}
                    </div>
                  </div>
                  <div className="kind">
                    {entryKind(row.section, entry)}
                    {entry.values && <small>{entry.values.join(', ')}</small>}
                    {entry.validation && (entry.validation.min !== undefined || entry.validation.max !== undefined) && (
                      <small>
                        {entry.validation.min !== undefined ? `from ${entry.validation.min}` : ''}
                        {entry.validation.max !== undefined ? ` up to ${entry.validation.max}` : ''}
                      </small>
                    )}
                  </div>
                  <div className="key">
                    {entry.booking_key ? <>Books into <code>{entry.booking_key}</code></> : 'Read only, not booked'}
                    <small>{CITATION_WORDS[entry.citation || 'OPTIONAL']}</small>
                  </div>
                  <div className="instruction">
                    {entry.instruction || <span style={{ color: '#7d8ea3' }}>No instruction</span>}
                    {entry.item_fields && (
                      <small style={{ display: 'block', color: '#91a5bd', marginTop: 4 }}>
                        {entry.item_fields.map(item => item.label).join(' · ')}
                      </small>
                    )}
                    {own.map(problem => <div className="problem" key={problem.pointer + problem.message}>{problem.message}</div>)}
                  </div>
                  <div className="tools">
                    {editable && (
                      <>
                        <SmallButton type="button" aria-label={`Change ${entry.label}`} onClick={() => open(key, entry, group)}>
                          <Pencil size={14} />
                        </SmallButton>
                        <SmallButton type="button" aria-label={`Remove ${entry.label}`} onClick={() => remove(row)}>
                          <Trash2 size={14} />
                        </SmallButton>
                      </>
                    )}
                  </div>
                </div>
                {editing === key && renderEditor(row.section, row.index)}
              </React.Fragment>
            );
          })}
        </Group>
      ))}

      {editable && (
        editing === 'new'
          ? <Group><h3>New field</h3>{renderEditor('fields', null)}</Group>
          : (
            <SmallButton type="button" onClick={() => open('new', null, groupNames[0] || 'Other')} style={{ marginBottom: 16 }}>
              <Plus size={14} /> Add a field
            </SmallButton>
          )
      )}

      <CrossFieldChecksPanel template={template} editable={editable} problems={problems} onChange={onChange} />
    </div>
  );
};

export default TemplateFieldsPanel;
